<template>
  <div class="customer-pool-dashboard" v-loading="loading">
    <section class="dashboard-section">
      <div class="section-title">
        实时概览
        <span class="scope-tag">{{ scopeLabel }}</span>
      </div>
      <el-alert
        class="view-alert"
        type="info"
        :closable="false"
        show-icon
        :title="viewerDescription"
      />
      <el-row :gutter="16" class="metric-cards">
        <el-col :span="6" v-for="metric in realtimeMetricCards" :key="metric.label">
          <el-card shadow="hover">
            <div class="metric-label">
              {{ metric.label }}
              <el-tooltip v-if="metric.hint" effect="light" placement="right" :content="metric.hint">
                <i class="metric-info el-icon-info"></i>
              </el-tooltip>
            </div>
            <div class="metric-value">{{ metric.value }}</div>
          </el-card>
        </el-col>
      </el-row>

      <div class="filter-bar">
        <el-date-picker
          v-model="dateRange"
          type="daterange"
          range-separator="至"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          value-format="yyyy-MM-dd"
          @change="fetchData"
        />
        <el-tooltip effect="light" placement="right" :content="dateRangeHint">
          <i class="metric-info el-icon-info"></i>
        </el-tooltip>
      </div>
    </section>

    <section v-if="isMemberView" class="dashboard-section">
      <div class="section-title">我的认领执行</div>
      <el-row :gutter="16" class="metric-cards">
        <el-col :span="6" v-for="metric in memberMetricCards" :key="metric.label">
          <el-card shadow="hover">
            <div class="metric-label">
              {{ metric.label }}
              <el-tooltip v-if="metric.hint" effect="light" placement="right" :content="metric.hint">
                <i class="metric-info el-icon-info"></i>
              </el-tooltip>
            </div>
            <div class="metric-value">{{ metric.value }}</div>
          </el-card>
        </el-col>
      </el-row>

      <el-row :gutter="16">
        <el-col :span="12">
          <el-card class="dashboard-card" shadow="never">
            <div slot="header">我的容量状态</div>
            <el-table :data="capacityRows" size="small" empty-text="暂无容量数据">
              <el-table-column prop="usage" label="当前持有" width="100" align="right" />
              <el-table-column prop="limit" label="容量上限" width="100" align="right" />
              <el-table-column label="使用率" min-width="180">
                <template slot-scope="{ row }">
                  <el-progress
                    :percentage="percentNumber(row.usage_ratio)"
                    :status="row.full ? 'exception' : (row.warning_reached ? 'warning' : undefined)"
                  />
                </template>
              </el-table-column>
              <el-table-column label="状态" width="100">
                <template slot-scope="{ row }">
                  <el-tag v-if="row.full" type="danger" size="small">已满</el-tag>
                  <el-tag v-else-if="row.warning_reached" type="warning" size="small">预警</el-tag>
                  <el-tag v-else type="success" size="small">正常</el-tag>
                </template>
              </el-table-column>
            </el-table>
          </el-card>
        </el-col>

        <el-col :span="12">
          <el-card class="dashboard-card" shadow="never">
            <div slot="header">我的客户流出原因</div>
            <el-table :data="stats.release_reason_distribution || []" size="small" empty-text="暂无流出记录">
              <el-table-column prop="reason" label="原因" min-width="180" />
              <el-table-column prop="count" label="客户数" width="100" align="right" />
            </el-table>
          </el-card>
        </el-col>
      </el-row>
    </section>

    <section v-else class="dashboard-section">
      <div class="section-title">流转运营</div>
      <el-row :gutter="16" class="metric-cards">
        <el-col :span="6" v-for="metric in managementFlowCards" :key="metric.label">
          <el-card shadow="hover">
            <div class="metric-label">
              {{ metric.label }}
              <el-tooltip v-if="metric.hint" effect="light" placement="right" :content="metric.hint">
                <i class="metric-info el-icon-info"></i>
              </el-tooltip>
            </div>
            <div class="metric-value">{{ metric.value }}</div>
          </el-card>
        </el-col>
      </el-row>

      <div class="section-title sub-title">认领后经营</div>
      <el-row :gutter="16" class="metric-cards">
        <el-col :span="6" v-for="metric in managementClaimCards" :key="metric.label">
          <el-card shadow="hover">
            <div class="metric-label">
              {{ metric.label }}
              <el-tooltip v-if="metric.hint" effect="light" placement="right" :content="metric.hint">
                <i class="metric-info el-icon-info"></i>
              </el-tooltip>
            </div>
            <div class="metric-value">{{ metric.value }}</div>
          </el-card>
        </el-col>
      </el-row>

      <el-card class="dashboard-card" shadow="never">
        <div slot="header">容量明细</div>
        <el-table :data="capacityRows" size="small" empty-text="暂无容量数据">
          <el-table-column prop="team_name" label="团队" min-width="140">
            <template slot-scope="{ row }">{{ row.team_name || '-' }}</template>
          </el-table-column>
          <el-table-column prop="user_name" label="业务员" min-width="120" />
          <el-table-column prop="usage" label="当前持有" width="100" align="right" />
          <el-table-column prop="limit" label="容量上限" width="100" align="right" />
          <el-table-column label="使用率" min-width="180">
            <template slot-scope="{ row }">
              <el-progress
                :percentage="percentNumber(row.usage_ratio)"
                :status="row.full ? 'exception' : (row.warning_reached ? 'warning' : undefined)"
              />
            </template>
          </el-table-column>
          <el-table-column label="状态" width="100">
            <template slot-scope="{ row }">
              <el-tag v-if="row.full" type="danger" size="small">已满</el-tag>
              <el-tag v-else-if="row.warning_reached" type="warning" size="small">预警</el-tag>
              <el-tag v-else type="success" size="small">正常</el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <el-row :gutter="16">
        <el-col :span="12">
          <el-card class="dashboard-card" shadow="never">
            <div slot="header">释放原因分布</div>
            <el-table :data="stats.release_reason_distribution || []" size="small" empty-text="暂无释放记录">
              <el-table-column prop="reason" label="原因" min-width="180" />
              <el-table-column prop="count" label="客户数" width="100" align="right" />
            </el-table>
          </el-card>
        </el-col>

        <el-col :span="12">
          <el-card class="dashboard-card" shadow="never">
            <div slot="header">区间流转排名（被流转客户数）</div>
            <el-table :data="stats.flow_ranking || []" size="small" empty-text="暂无数据">
              <el-table-column type="index" label="排名" width="80" />
              <el-table-column prop="team_name" label="团队" min-width="140" />
              <el-table-column prop="user_name" label="业务员" min-width="120" />
              <el-table-column prop="flowed_count" label="被流转客户数" min-width="120" align="right" />
            </el-table>
          </el-card>
        </el-col>
      </el-row>
    </section>
  </div>
</template>

<script>
import { Vue, Component } from 'vue-property-decorator';
import { request } from 'beans-admin-plugin';
import moment from 'moment';

@Component
export default class CustomerPoolDashboard extends Vue {
  loading = false;
  dateRange = [moment().startOf('month').format('YYYY-MM-DD'), moment().format('YYYY-MM-DD')];
  stats = {};

  get viewerContext() {
    return this.stats.viewer_context || {};
  }

  get viewerRole() {
    return this.viewerContext.role || 'member';
  }

  get isMemberView() {
    return this.viewerRole === 'member';
  }

  get scopeLabel() {
    return this.viewerContext.scope_label || (this.isMemberView ? '我' : '管理范围');
  }

  get viewerDescription() {
    if (this.viewerRole === 'application_admin') {
      return '当前为管理员视角：重点观察全应用业务人员的客户流入公海、认领转化、容量风险和异常流出。';
    }
    if (this.viewerRole === 'team_leader') {
      return '当前为队长视角：重点观察团队客户流出原因、认领后经营结果和成员容量风险。';
    }
    return '当前为个人视角：重点观察我的认领执行、首次有效跟进、成交转化和个人容量状态。';
  }

  get dateRangeHint() {
    if (this.isMemberView) {
      return '用于统计所选日期区间内我的认领、首次有效跟进、成交转化和客户流出；实时概览不受日期影响。';
    }
    return '用于统计所选日期区间内管理范围的客户进池、认领、首次有效跟进、成交转化、释放原因和流转排名；实时概览不受日期影响。';
  }

  get capacityRows() {
    return this.stats.capacity_rows || [];
  }

  get memberCapacity() {
    return this.capacityRows[0] || {};
  }

  get capacityWarningCount() {
    return this.capacityRows.filter(row => row.warning_reached).length;
  }

  get capacityFullCount() {
    return this.capacityRows.filter(row => row.full).length;
  }

  get realtimeMetricCards() {
    const s = this.stats;
    if (this.isMemberView) {
      return [
        { label: '当前可认领池客户数', value: s.current_pool_count || 0, hint: '当前你有权限看到、仍在公海/流转池中且尚未被认领的客户数' },
        { label: '我当前持有客户数', value: this.memberCapacity.usage || 0, hint: '当前归属在你名下且处于已认领状态的客户数' },
        { label: '我的容量上限', value: this.memberCapacity.limit || 0, hint: '个人配置优先；未配置时按团队身份默认容量计算' },
        { label: '我的容量状态', value: this.capacityStatusText(this.memberCapacity), hint: '容量满时无法继续新增或认领客户' },
      ];
    }
    return [
      { label: '当前池内客户总数', value: s.current_pool_count || 0, hint: '当前仍在公海/流转池中，尚未被认领的客户数' },
      { label: '管理成员数', value: this.viewerContext.managed_member_count || 0, hint: `当前统计范围：${this.scopeLabel}` },
      { label: '容量预警人数', value: this.capacityWarningCount, hint: '当前持有客户数达到容量预警比例的成员数' },
      { label: '容量已满人数', value: this.capacityFullCount, hint: '当前持有客户数已达到容量上限的成员数' },
    ];
  }

  get managementFlowCards() {
    const s = this.stats;
    return [
      { label: '区间进池客户数', value: s.entered_pool_count || 0, hint: `所选区间内从${this.scopeLabel}名下进入公海/流转池的客户数，包含手动释放、强制流转、自动释放、认领超时退回；转接不计入` },
      { label: '进池后已认领客户数', value: s.claimed_count || 0, hint: `上述进池客户中，进池后被${this.scopeLabel}认领的客户数` },
      { label: '流转认领率', value: this.toPercent(s.claim_rate), hint: '进池后已认领客户数 / 区间进池客户数，用于判断池内客户是否被及时回收' },
      { label: '平均客户生命周期(天)', value: s.avg_lifecycle_days || 0, hint: '区间进池客户从录入日期到进池日期的平均天数，用于观察客户在归属人手中停留多久后流出' },
    ];
  }

  get managementClaimCards() {
    const s = this.stats;
    return [
      { label: '区间认领客户数', value: s.followup_claimed_count || 0, hint: `认领时间落在所选区间内，且认领人为${this.scopeLabel}的客户数` },
      { label: '按时首跟客户数', value: s.followup_completed_count || 0, hint: '区间认领客户中，在配置期限内完成有效跟进的客户数' },
      { label: '首次有效跟进完成率', value: this.toPercent(s.followup_completion_rate), hint: '按时首跟客户数 / 区间认领客户数' },
      { label: '区间认领后成交客户数', value: s.period_claim_activated_count || 0, hint: '区间认领客户中，认领后产生正常销售订单的客户数' },
      { label: '区间认领后成交率', value: this.toPercent(s.period_claim_activation_rate), hint: '区间认领后成交客户数 / 区间认领客户数' },
    ];
  }

  get memberMetricCards() {
    const s = this.stats;
    return [
      { label: '我区间认领客户数', value: s.followup_claimed_count || 0, hint: '认领时间落在所选区间内的客户数，这是个人执行看板的核心分母' },
      { label: '我按时首跟客户数', value: s.followup_completed_count || 0, hint: '区间认领客户中，在配置期限内完成有效跟进的客户数' },
      { label: '我的首次有效跟进完成率', value: this.toPercent(s.followup_completion_rate), hint: '我按时首跟客户数 / 我区间认领客户数' },
      { label: '我认领后成交客户数', value: s.period_claim_activated_count || 0, hint: '我区间认领客户中，认领后产生正常销售订单的客户数' },
      { label: '我的认领后成交率', value: this.toPercent(s.period_claim_activation_rate), hint: '我认领后成交客户数 / 我区间认领客户数' },
      { label: '我的客户流出数', value: s.entered_pool_count || 0, hint: '所选区间内从我名下进入公海/流转池的客户数，包含手动释放、强制流转、自动释放、认领超时退回；转接不计入' },
    ];
  }

  capacityStatusText(row) {
    if (row.full) {
      return '已满';
    }
    if (row.warning_reached) {
      return '预警';
    }
    return '正常';
  }

  toPercent(value) {
    return `${((value || 0) * 100).toFixed(1)}%`;
  }

  percentNumber(value) {
    return Math.min(Math.round((value || 0) * 100), 100);
  }

  mounted() {
    this.fetchData();
  }

  async fetchData() {
    if (!this.dateRange || this.dateRange.length !== 2) {
      return;
    }
    this.loading = true;
    try {
      const stats = await request.get('/dashboard/customer_pool', {
        created_gteq_date: this.dateRange[0],
        created_lteq_date: this.dateRange[1],
      });
      this.stats = stats || {};
    } finally {
      this.loading = false;
    }
  }
}

export const route = [
  {
    path: '/customer_pool_dashboard',
    name: 'customer_pool_dashboard.index',
    component: CustomerPoolDashboard,
  },
];
</script>

<style scoped>
.customer-pool-dashboard {
  padding: 16px;
}
.dashboard-section {
  margin-bottom: 24px;
}
.section-title {
  align-items: center;
  display: flex;
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;
}
.sub-title {
  margin-top: 8px;
}
.scope-tag {
  background: #ecf5ff;
  border-radius: 12px;
  color: #409eff;
  font-size: 12px;
  font-weight: 400;
  margin-left: 8px;
  padding: 2px 8px;
}
.view-alert {
  margin-bottom: 16px;
}
.filter-bar {
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 16px;
}
.metric-cards {
  display: flex;
  flex-wrap: wrap;
  margin-bottom: 16px;
}
.metric-cards .el-col {
  display: flex;
  float: none;
  margin-bottom: 16px;
}
.metric-cards .el-card {
  min-height: 132px;
  width: 100%;
}
.metric-label {
  color: #909399;
  font-size: 13px;
}
.metric-value {
  font-size: 24px;
  font-weight: 600;
  margin-top: 8px;
}
.metric-info {
  color: #101ebf;
  font-size: 16px;
  margin-left: 4px;
}
.dashboard-card {
  margin-bottom: 16px;
}
</style>
